import { Stack, Card, Box, Collapse } from '@mui/material';
import { Typography, Button, colors } from '@sweep-io/sweep-design';
import IssuesTableWithFilters from './IssuesTableWithFilters';
import IssuesTableImage from './issuesTableImage.svg?react';
import { Issue } from './issuesTypes';
import { ActiveScan, ScanStatus } from '@server/issues';
import { OnUpdateIssueStatus } from '../aiAgentCenter/aiAgentsConsts';
import useAgentsHome from '../aiAgentCenter/useAgentsHome';
import { useCallback, useState, useEffect, useRef } from 'react';
import ScanningIndicator from './ScanningIndicator';
import { CenteredCircularProgress } from '../common/CenteredCircularProgress';

interface IssuesCardProps {
  issues?: Issue[];
  objectLabel: string;
  isLoading: boolean;
  activeScan?: ActiveScan;
  onUpdateIssueStatus: OnUpdateIssueStatus;
  onTextSelection?: (text: string | null) => void;
  onCardExpandClick?: (isExpanded: boolean) => void;
  tableInitialHeight?: number;
}

const IssuesCard: React.FC<IssuesCardProps> = ({
  issues,
  objectLabel,
  isLoading,
  activeScan,
  onUpdateIssueStatus,
  onTextSelection,
  onCardExpandClick,
  tableInitialHeight,
}) => {
  const { isExpanded: isPanelExpanded, setIsExpanded } = useAgentsHome();
  const [isCardExpanded, setIsCardExpanded] = useState(false);
  const [expandedRowId, setExpandedRowId] = useState<string>();
  const [shouldScrollToRow, setShouldScrollToRow] = useState(false);
  const tableRef = useRef<any>(null);
  const issuesCount = issues?.length;
  const isScanInProgress = activeScan && activeScan?.status !== ScanStatus.DONE;
  const summaryText =
    isScanInProgress && !issuesCount
      ? undefined
      : `${issuesCount || 'No'} issues found ${objectLabel ? `in the ${objectLabel} object` : ''}`;

  // Effect to scroll to expanded row after panel expansion
  useEffect(() => {
    if (shouldScrollToRow && expandedRowId && isPanelExpanded && tableRef.current) {
      // Find the index of the expanded row in the issues array
      const rowIndex = issues?.findIndex((issue) => issue.id === expandedRowId);
      if (rowIndex !== undefined && rowIndex >= 0) {
        // For expandable rows, we need to account for the fact that each row is duplicated
        // Base row is at index * 2, expanded row is at index * 2 + 1
        const baseRowIndex = rowIndex * 2;

        // Use setTimeout to ensure the table has fully rendered
        setTimeout(() => {
          tableRef.current?.scrollToIndex?.({
            index: baseRowIndex,
            align: 'center',
            behavior: 'smooth',
          });
        }, 200);
      }
      setShouldScrollToRow(false);
    }
  }, [shouldScrollToRow, expandedRowId, isPanelExpanded, issues]);

  const handleCardExpandClick = () => {
    setIsCardExpanded(true);
    onCardExpandClick?.(true);
  };

  const handleCardCollapseClick = () => {
    setIsCardExpanded(false);
    onCardExpandClick?.(false);
  };

  const onExpandTableRow = useCallback(() => {
    setIsExpanded({ isExpanded: true });
    setShouldScrollToRow(true);
  }, [setIsExpanded]);

  return (
    <Stack
      sx={{
        width: '100%',
        transition: 'all 0.3s ease-in-out',
      }}
    >
      {/* Collapsed State */}
      <Collapse in={!isCardExpanded} timeout={300}>
        <Card
          elevation={0}
          sx={{
            pointerEvents: isLoading ? 'none' : 'initial',
            width: '100%',
            borderRadius: '8px',
            padding: isPanelExpanded ? '16px 24px' : '16px',
            display: 'flex',
            justifyContent: 'space-between',
            position: 'relative',
            height: isPanelExpanded ? '80px' : '129px',
            transition: 'all 0.3s ease-in-out',
            opacity: isCardExpanded ? 0 : 1,
            transform: isCardExpanded ? 'translateY(-10px)' : 'translateY(0)',
            '.image-container': {
              transform: 'rotateZ(0)',
              transition: 'transform 0.2s ease-in-out',
              bottom: '-4px',
            },
            '&:hover .image-container': {
              transform: 'rotateZ(2deg)',
            },
          }}
          onClick={handleCardExpandClick}
        >
          {isLoading && (
            <Stack justifyContent={isPanelExpanded ? 'center' : 'flex-start'}>
              <Stack direction="row" gap={1} alignItems="center">
                <Typography variant="body" color={colors.grey[700]}>
                  Scanning for issues
                </Typography>
                <CenteredCircularProgress size={10} />
              </Stack>
            </Stack>
          )}
          {!isLoading && (
            <Stack gap={0.5} justifyContent="center">
              {summaryText && (
                <Typography variant="body" color={colors.grey[700]}>
                  {summaryText}
                </Typography>
              )}
              {isScanInProgress ? (
                <ScanningIndicator />
              ) : (
                <Button variant="flat-blue" endIconName="ChevronDown">
                  Expand to view details
                </Button>
              )}
            </Stack>
          )}
          <Box
            className="image-container"
            sx={{
              position: 'absolute',
              bottom: 0,
              right: isPanelExpanded ? '30px' : '16px',
              height: '61px',
              svg: {
                height: '61px',
              },
            }}
          >
            <IssuesTableImage />
          </Box>
        </Card>
      </Collapse>

      {/* Expanded State */}
      <Collapse in={isCardExpanded} timeout={300}>
        <Stack
          sx={{
            gap: isScanInProgress ? 0.5 : 2,
            width: '100%',
            opacity: isCardExpanded ? 1 : 0,
            transform: isCardExpanded ? 'translateY(0)' : 'translateY(-10px)',
            transition: 'opacity 0.3s ease-in-out, transform 0.3s ease-in-out',
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="body" color={colors.grey[700]}>
              {summaryText}
            </Typography>
            <Button variant="flat-blue" onClick={handleCardCollapseClick} endIconName="ChevronUp">
              Collapse table
            </Button>
          </Stack>
          <IssuesTableWithFilters
            onUpdateIssueStatus={onUpdateIssueStatus}
            issues={issues ?? []}
            activeScan={activeScan}
            onTextSelection={onTextSelection}
            isPreviewMode={!isPanelExpanded}
            tableInitialHeight={tableInitialHeight}
            onExpandTableRow={onExpandTableRow}
            expandedRowId={expandedRowId}
            setExpandedRowId={setExpandedRowId}
            tableRef={tableRef}
          />
        </Stack>
      </Collapse>
    </Stack>
  );
};
export default IssuesCard;
